import React, { useState, useEffect } from "react";
import { Row, Col, Card, Button, Form, Spinner, Badge } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import PageTitle from "../../layouts/PageTitle";
import api from "../../../api";
import {
 getEquipment,
 deployEquipment,
 returnEquipment,
} from "../../../services/equipmentService";

const STATUS_COLORS = {
 Available: "success",
 Deployed: "primary",
 "Under Maintenance": "warning",
 Idle: "secondary",
 Retired: "dark",
 "In Transit": "info",
};

const EquipmentDeploy = ({ mode }) => {
 const navigate = useNavigate();
 const { id } = useParams();
 const isReturn = mode === "return";

 const [equipment, setEquipment] = useState(null);
 const [loading, setLoading] = useState(false);
 const [fetching, setFetching] = useState(true);
 const [projects, setProjects] = useState([]);
 const [workOrders, setWorkOrders] = useState([]);
 const [users, setUsers] = useState([]);

 const [form, setForm] = useState({
 project: "",
 workOrder: "",
 operator: "",
 location: "",
 deployedFrom: new Date().toISOString().slice(0, 10),
 purpose: "",
 remarks: "",
 // For return:
 returnDate: new Date().toISOString().slice(0, 10),
 utilizationHours: "",
 newStatus: "Available",
 });

 useEffect(() => {
 const load = async () => {
 try {
 const [eRes, pRes, uRes] = await Promise.all([
 getEquipment(id),
 api.get("/projects", { params: { limit: 200 } }),
 api.get("/users", { params: { limit: 200 } }),
 ]);
 const eq = eRes.data?.data;
 setEquipment(eq);
 setProjects(pRes.data?.data || pRes.data?.projects || []);
 setUsers(uRes.data?.data || uRes.data?.users || []);

 // Pre-fill for return
 if (isReturn && eq) {
 setForm((prev) => ({
 ...prev,
 project: eq.project?._id || eq.project || "",
 workOrder: eq.workOrder?._id || eq.workOrder || "",
 operator: eq.operator?._id || eq.operator || "",
 location: eq.location || "",
 }));
 }
 } catch (_) {
 toast.error("Failed to load data");
 } finally {
 setFetching(false);
 }
 };
 load();
 }, [id, isReturn]);

 useEffect(() => {
    const params = form.project ? { project: form.project, limit: 1000 } : { limit: 1000 };
    api
      .get("/work-orders", { params })
      .then((res) => setWorkOrders(res.data?.data || []))
      .catch(() => {});
  }, [form.project]);

   const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "project") {
        if (value && prev.workOrder) {
          const currentWo = workOrders.find((w) => String(w._id) === String(prev.workOrder));
          const currentWoProjId = currentWo?.project?._id || currentWo?.project;
          if (currentWoProjId && String(currentWoProjId) !== String(value)) {
            next.workOrder = "";
          }
        }
      } else if (name === "workOrder") {
        if (value) {
          const selectedWo = workOrders.find((w) => String(w._id) === String(value));
          const autoProjId = selectedWo?.project?._id || selectedWo?.project;
          if (autoProjId) {
            next.project = String(autoProjId);
          }
        }
      }
      return next;
    });
  };

 const handleSubmit = async (e) => {
 e.preventDefault();

 if (!isReturn && !form.project) {
 toast.error("Please select a project");
 return;
 }

 setLoading(true);
 try {
 if (isReturn) {
 await returnEquipment(id, {
 returnDate: form.returnDate,
 utilizationHours: form.utilizationHours,
 remarks: form.remarks,
 newStatus: form.newStatus,
 });
 toast.success("Equipment returned successfully");
 } else {
 await deployEquipment(id, {
 project: form.project,
 workOrder: form.workOrder || undefined,
 operator: form.operator || undefined,
 location: form.location,
 deployedFrom: form.deployedFrom,
 purpose: form.purpose,
 remarks: form.remarks,
 });
 toast.success("Equipment deployed successfully");
 }
 navigate("../equipment");
 } catch (err) {
 toast.error(err?.response?.data?.message || "Action failed");
 } finally {
 setLoading(false);
 }
 };

 if (fetching) {
 return (
 <div className="text-center py-5">
 <Spinner animation="border" variant="primary" />
 </div>
 );
 }

 return (
 <>
 <PageTitle
 activeMenu={isReturn ? "Return Equipment" : "Deploy Equipment"}
 motherMenu="Equipment"
 />

 {equipment && (
 <Card className="shadow-sm mb-3 border-start border-4 border-primary">
 <Card.Body className="py-3">
 <div className="d-flex align-items-center gap-3 flex-wrap">
 <div>
 <h5 className="mb-0 fw-bold">{equipment.name}</h5>
 <p className="text-muted mb-0 small">
 {equipment.equipmentCode} · {equipment.category}
 </p>
 </div>
 <Badge bg={STATUS_COLORS[equipment.status] || "secondary"} className="fs-6">
 {equipment.status}
 </Badge>
 </div>
 </Card.Body>
 </Card>
 )}

 <Card className="shadow-sm">
 <Card.Header>
 <h5 className="mb-0">
 <i className={`fas ${isReturn ? "fa-undo" : "fa-truck"} me-2 text-${isReturn ? "success" : "primary"}`} />
 {isReturn ? "Return Equipment" : "Deploy Equipment"}
 </h5>
 </Card.Header>
 <Card.Body>
 <Form onSubmit={handleSubmit}>
 {!isReturn ? (
 <>
 <Row className="mb-3">
 <Col md={6}>
 <Form.Group>
 <Form.Label>Project <span className="text-danger">*</span></Form.Label>
 <Form.Select name="project" value={form.project} onChange={handleChange} required>
 <option value="">— Select Project —</option>
 {projects.map((p) => (
 <option key={p._id} value={p._id}>{p.projectName || p.name}</option>
 ))}
 </Form.Select>
 </Form.Group>
 </Col>
 <Col md={6}>
 <Form.Group>
 <Form.Label>Work Order</Form.Label>
 <Form.Select name="workOrder" value={form.workOrder} onChange={handleChange}>
 <option value="">— Select Work Order —</option>
 {workOrders.map((wo) => (
 <option key={wo._id} value={wo._id}>{wo.woNo}{wo.label ? ` – ${wo.label}` : ""}</option>
 ))}
 </Form.Select>
 </Form.Group>
 </Col>
 </Row>

 <Row className="mb-3">
 <Col md={4}>
 <Form.Group>
 <Form.Label>Operator</Form.Label>
 <Form.Select name="operator" value={form.operator} onChange={handleChange}>
 <option value="">— Select Operator —</option>
 {users.map((u) => (
 <option key={u._id} value={u._id}>{u.name}</option>
 ))}
 </Form.Select>
 </Form.Group>
 </Col>
 <Col md={4}>
 <Form.Group>
 <Form.Label>Deployed From</Form.Label>
 <Form.Control
 type="date"
 name="deployedFrom"
 value={form.deployedFrom}
 onChange={handleChange}
 />
 </Form.Group>
 </Col>
 <Col md={4}>
 <Form.Group>
 <Form.Label>Location / Site</Form.Label>
 <Form.Control
 name="location"
 value={form.location}
 onChange={handleChange}
 placeholder="Deployment site"
 />
 </Form.Group>
 </Col>
 </Row>

 <Row className="mb-3">
 <Col md={6}>
 <Form.Group>
 <Form.Label>Purpose</Form.Label>
 <Form.Control
 name="purpose"
 value={form.purpose}
 onChange={handleChange}
 placeholder="Purpose of deployment"
 />
 </Form.Group>
 </Col>
 <Col md={6}>
 <Form.Group>
 <Form.Label>Remarks</Form.Label>
 <Form.Control
 name="remarks"
 value={form.remarks}
 onChange={handleChange}
 placeholder="Additional notes"
 />
 </Form.Group>
 </Col>
 </Row>
 </>
 ) : (
 <>
 <Row className="mb-3">
 <Col md={4}>
 <Form.Group>
 <Form.Label>Return Date</Form.Label>
 <Form.Control
 type="date"
 name="returnDate"
 value={form.returnDate}
 onChange={handleChange}
 />
 </Form.Group>
 </Col>
 <Col md={4}>
 <Form.Group>
 <Form.Label>Utilization Hours (this deployment)</Form.Label>
 <Form.Control
 type="number"
 name="utilizationHours"
 value={form.utilizationHours}
 onChange={handleChange}
 placeholder="e.g. 48"
 min="0"
 step="0.5"
 />
 </Form.Group>
 </Col>
 <Col md={4}>
 <Form.Group>
 <Form.Label>Return Status</Form.Label>
 <Form.Select name="newStatus" value={form.newStatus} onChange={handleChange}>
 {["Available", "Under Maintenance", "Idle", "In Transit"].map((s) => (
 <option key={s} value={s}>{s}</option>
 ))}
 </Form.Select>
 </Form.Group>
 </Col>
 </Row>

 <Row className="mb-3">
 <Col>
 <Form.Group>
 <Form.Label>Return Remarks</Form.Label>
 <Form.Control
 as="textarea"
 rows={3}
 name="remarks"
 value={form.remarks}
 onChange={handleChange}
 placeholder="Condition notes, issues found, etc."
 />
 </Form.Group>
 </Col>
 </Row>
 </>
 )}

 <div className="d-flex gap-2">
 <Button type="submit" variant={isReturn ? "success" : "primary"} disabled={loading}>
 {loading ? (
 <><Spinner size="sm" className="me-2" />{isReturn ? "Returning..." : "Deploying..."}</>
 ) : (
 isReturn ? "Confirm Return" : "Deploy Equipment"
 )}
 </Button>
 <Button variant="outline-secondary" onClick={() => navigate("../equipment")}>
 Cancel
 </Button>
 </div>
 </Form>
 </Card.Body>
 </Card>
 </>
 );
};

export default EquipmentDeploy;
